import { fileUrl } from "@/lib/api";
import { normalizeMedia, videoEmbed, optimizedImg, useIsMobile } from "@/lib/media";

// Renders a CMS media value (string or {image, image_mobile, video, video_mobile, overlay}).
// Video → muted looping background. Image → responsive <picture> with mobile optimization.
// overlay (0-100) → black scrim on top of the media for text readability.
export default function SmartMedia({ media, alt = "", className = "", eager = false }) {
  const m = normalizeMedia(media);
  const isMobile = useIsMobile();
  const video = isMobile && m.video_mobile ? m.video_mobile : m.video;
  const scrim = m.overlay > 0 ? (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ background: "#000", opacity: Math.min(m.overlay, 90) / 100 }}
      data-testid="smart-media-overlay"
    />
  ) : null;

  if (video) {
    const embed = videoEmbed(video, { start: m.video_start, end: m.video_end });
    if (embed) {
      const wrapCls = /\babsolute\b/.test(className) ? className : `relative ${className}`;
      const killCaptions = (e) => {
        const win = e.currentTarget?.contentWindow;
        if (!win) return;
        const send = (func, args = []) => {
          try {
            win.postMessage(JSON.stringify({ event: "command", func, args }), "*");
          } catch (_) { /* noop */ }
        };
        // YouTube IFrame API: turn off captions on load and again shortly after
        // (some videos auto-load captions a moment after playback starts).
        const off = () => {
          send("unloadModule", ["captions"]);
          send("unloadModule", ["cc"]);
          send("setOption", ["captions", "track", {}]);
          send("setOption", ["cc", "track", {}]);
        };
        off();
        setTimeout(off, 800);
        setTimeout(off, 2500);
      };
      return (
        <div className={`${wrapCls} overflow-hidden pointer-events-none`} data-testid="smart-media-embed">
          <iframe
            src={embed}
            title={alt || "Video"}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-0"
            style={{ width: "max(100%, 177.78vh)", height: "max(100%, 56.25vw)" }}
            allow="autoplay; encrypted-media; picture-in-picture"
            tabIndex={-1}
            aria-hidden="true"
            onLoad={killCaptions}
          />
          {scrim}
        </div>
      );
    }
    return (
      <>
        <video
          key={video}
          className={className}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={m.image ? fileUrl(m.image) : undefined}
          data-testid="smart-media-video"
        >
          <source src={fileUrl(video)} />
        </video>
        {scrim}
      </>
    );
  }

  if (!m.image) return null;
  const mobileSrc = m.image_mobile ? fileUrl(m.image_mobile) : optimizedImg(m.image, 828);
  return (
    <>
      <picture>
        <source media="(max-width: 767px)" srcSet={mobileSrc} />
        <img src={fileUrl(m.image)} alt={alt} className={className} loading={eager ? "eager" : "lazy"} />
      </picture>
      {scrim}
    </>
  );
}
